
import Layout from '@/components/Layout';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Home, Users, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const PublishProperty = () => {
  const { language } = useLanguage();

  // Construir ruta localizada
  const getLocalizedPath = (path: string) => {
    return language === 'en' ? `/en${path}` : path;
  };

  const benefits = [
    "Publicación gratuita en estanciacorta.com",
    "Sincronización automática desde Airbnb",
    "Solo cobramos 15% por reservación completada",
    "Tú mantienes el control total de la gestión",
    "Acceso a nuestra plataforma de huéspedes",
    "Sin costos ocultos ni comisiones adicionales"
  ];

  return (
    <Layout>
      <SEOHead 
        title="Publica tu Propiedad - Estancia Corta"
        description="Publica tu propiedad sin costo en Estancia Corta. Solo cobramos 15% por reservación completada. Si ya tienes Airbnb, nosotros la subimos por ti."
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Publica tu propiedad <br />
            <span className="text-blue-200">sin costo</span>
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Únete y llena tus fechas vacias, ¡genera más ingresos con antelación!
          </p>
          <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 text-white border border-white/20">
            <DollarSign className="w-5 h-5 mr-2 text-green-300" />
            <span className="font-semibold">
              Solo 15% por reservación completada
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                ¿Ya tienes tu propiedad en Airbnb?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Perfecto. Nosotros mismos podemos subir tu propiedad en Estancia Corta 
                sin que tengas que hacer trabajo adicional.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Cobramos únicamente el <strong>15% del costo total de la reservación</strong>, 
                pero una vez realizado el pago, <strong>tú como dueño te encargas del 
                seguimiento y gestión del huésped</strong>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Link to={getLocalizedPath('/contacto')} className="flex items-center">
                    <Home className="w-5 h-5 mr-2" />
                    Publicar mi propiedad
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Content - Benefits */}
            <div>
              <Card className="border-2 border-blue-100">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900 text-center">
                    Beneficios de publicar con nosotros
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-xl text-gray-600">
              Proceso simple y directo
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Contáctanos
              </h3>
              <p className="text-gray-600">
                Envíanos la información de tu propiedad y enlace de Airbnb
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nosotros la subimos
              </h3>
              <p className="text-gray-600">
                Creamos el listado en Estancia Corta con toda tu información
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Recibe huéspedes
              </h3>
              <p className="text-gray-600">
                Gestionas directamente a tus huéspedes y recibes pagos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para generar ingresos adicionales?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contáctanos hoy y comienza a recibir huéspedes en tu propiedad
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-50">
            <Link to={getLocalizedPath('/contacto')}>
              Publicar mi propiedad ahora
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default PublishProperty;
